/**
 * Pagination — page nav.
 *
 * Design system mapping:
 *   • Container: bg-webex-gray-50 dark:bg-webex-gray-900 border-t webex-gray-200 dark: webex-gray-800
 *   • Page link: text-sm text-webex-gray-1000 dark: white px-3 py-2 border-b-4 border-transparent
 *     active: border-b-webex-gray-1000 dark:border-b-white text-webex-gray-1000 dark:white
 *     hover: border-b-webex-gray-300 dark:border-b-webex-gray-600
 *   • Disabled: text-webex-gray-400 dark: webex-gray-500 cursor-default
 */
import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type JSX,
} from "react"
import { cn } from "@lib/cn"
import { Button } from "./Button"
import { Input } from "./Input"
import { Select } from "./Select"

interface PaginationProps {
  currentPage?: number
  totalPages?: number
  totalRecords?: number
  onPageChange?: (page: number) => void
  showPerPageSelector?: boolean
  perPage?: number
  onPerPageChange?: (perPage: number) => void
  onLoadCount?: () => Promise<{ totalRecords: number; totalPages: number }>
  countLoading?: boolean
  countError?: string | null
  showJumpToPage?: boolean
  maxPageJump?: number
  className?: string
}

export function Pagination({
  currentPage = 1,
  totalPages,
  totalRecords,
  onPageChange,
  showPerPageSelector = false,
  perPage = 50,
  onPerPageChange,
  onLoadCount,
  countLoading = false,
  countError = null,
  showJumpToPage = true,
  maxPageJump = 1000,
  className,
}: PaginationProps) {
  const [jumpToPage, setJumpToPage] = useState<string>("")
  const [jumpError, setJumpError] = useState<string | null>(null)
  const [isLoadingCount, setIsLoadingCount] = useState(false)
  const [asyncTotalPages, setAsyncTotalPages] = useState<number | undefined>(totalPages)
  const [asyncTotalRecords, setAsyncTotalRecords] = useState<number | undefined>(totalRecords)
  const [asyncCountError, setAsyncCountError] = useState<string | null>(countError)

  const effectiveTotalPages = totalPages ?? asyncTotalPages
  const effectiveTotalRecords = totalRecords ?? asyncTotalRecords
  const effectiveCountLoading = countLoading || isLoadingCount
  const effectiveCountError = countError ?? asyncCountError

  useEffect(() => {
    if (onLoadCount && !effectiveTotalPages && !effectiveCountLoading && !effectiveCountError) {
      setIsLoadingCount(true)
      setAsyncCountError(null)
      onLoadCount()
        .then(({ totalRecords, totalPages }) => {
          setAsyncTotalRecords(totalRecords)
          setAsyncTotalPages(totalPages)
        })
        .catch((error: Error) => setAsyncCountError(error.message || "Failed to load count"))
        .finally(() => setIsLoadingCount(false))
    }
  }, [onLoadCount, effectiveTotalPages, effectiveCountLoading, effectiveCountError])

  useEffect(() => {
    if (totalPages !== undefined) setAsyncTotalPages(totalPages)
    if (totalRecords !== undefined) setAsyncTotalRecords(totalRecords)
    if (countError !== undefined) setAsyncCountError(countError)
  }, [totalPages, totalRecords, countError])

  const nextEnabled =
    effectiveTotalPages !== undefined && effectiveTotalPages !== null
      ? currentPage < effectiveTotalPages
      : true
  const prevEnabled = currentPage > 1

  const getVisiblePages = () => {
    if (!effectiveTotalPages || effectiveTotalPages < 1) return []
    if (effectiveTotalPages <= 10) {
      return Array.from({ length: effectiveTotalPages }, (_, i) => i + 1)
    }
    const visible = new Set<number>([
      1,
      effectiveTotalPages,
      currentPage,
      currentPage - 1,
      currentPage + 1,
    ])
    if (currentPage < 5) for (let i = 1; i <= 5; i++) visible.add(i)
    if (currentPage > effectiveTotalPages - 4)
      for (let i = effectiveTotalPages; i >= effectiveTotalPages - 4; i--) visible.add(i)
    return Array.from(visible)
      .filter((p) => p >= 1 && p <= effectiveTotalPages)
      .sort((a, b) => a - b)
  }

  const handleJump = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      setJumpError(null)
      const num = parseInt(jumpToPage)
      if (isNaN(num)) return setJumpError("Please enter a valid page number")
      if (num < 1) return setJumpError("Page number must be at least 1")
      if (effectiveTotalPages && num > effectiveTotalPages)
        return setJumpError(`Page number cannot exceed ${effectiveTotalPages}`)
      if (num > maxPageJump) return setJumpError(`Page number cannot exceed ${maxPageJump}`)
      if (num === currentPage) return setJumpError("You are already on this page")
      onPageChange?.(num)
      setJumpToPage("")
    },
    [jumpToPage, effectiveTotalPages, currentPage, maxPageJump, onPageChange],
  )

  const handleJumpInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    if (v === "" || /^\d+$/.test(v)) {
      setJumpToPage(v)
      setJumpError(null)
    }
  }

  const handleJumpInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleJump(e)
  }

  const PageInfo = () => {
    if (effectiveCountLoading) {
      return (
        <div className="flex items-center gap-2">
          <div className="h-4 w-16 bg-webex-gray-100 dark:bg-webex-gray-900 rounded animate-pulse" />
          <span className="text-xs text-webex-gray-600 dark:text-webex-gray-400">of</span>
          <div className="h-4 w-8 bg-webex-gray-100 dark:bg-webex-gray-900 rounded animate-pulse" />
        </div>
      )
    }
    if (effectiveCountError) {
      return (
        <div className="flex items-center gap-2 text-webex-red-700 dark:text-webex-red-400">
          <span className="text-sm text-webex-gray-1000 dark:text-white">Page {currentPage}</span>
          <span className="text-sm text-webex-gray-700 dark:text-webex-gray-300">of</span>
          <span className="text-sm text-webex-gray-1000 dark:text-white">?</span>
          <span className="text-xs">(Count unavailable)</span>
        </div>
      )
    }
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-webex-gray-1000 dark:text-white">Page {currentPage}</span>
        <span className="text-sm text-webex-gray-700 dark:text-webex-gray-300">of</span>
        <span className="text-sm text-webex-gray-1000 dark:text-white">
          {effectiveTotalPages ?? "?"}
        </span>
        {effectiveTotalRecords != null && effectiveTotalRecords > 0 && (
          <span className="text-xs text-webex-gray-600 dark:text-webex-gray-400">
            ({effectiveTotalRecords.toLocaleString()} records)
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between bg-webex-gray-50 dark:bg-webex-gray-900",
        "border-t border-webex-gray-200 dark:border-webex-gray-800",
        "pr-8",
        className,
      )}
    >
      {showPerPageSelector && (
        <div className="flex items-center gap-3 pl-5 py-2">
          <label htmlFor="per-page-select" className="text-sm font-medium text-webex-gray-1000 dark:text-white">
            Items per page:
          </label>
          <Select
            id="per-page-select"
            size="sm"
            defaultValue={String(perPage)}
            onChange={(e) => onPerPageChange?.(parseInt(e.target.value))}
            options={[
              { value: "5", label: "5" },
              { value: "10", label: "10" },
              { value: "15", label: "15" },
              { value: "30", label: "30" },
              { value: "50", label: "50" },
            ]}
            className="w-20"
          />
        </div>
      )}

      {showJumpToPage && (
        <div className="flex items-center gap-4 ml-auto py-2">
          <PageInfo />

          {effectiveTotalPages && effectiveTotalPages > 2 && (
            <div className="flex items-center gap-2">
              <form onSubmit={handleJump} className="flex items-center gap-2">
                <label htmlFor="jump-to-page-input" className="text-sm text-webex-gray-800 dark:text-webex-gray-200">
                  Go to page:
                </label>
                <Input
                  id="jump-to-page-input"
                  type="text"
                  size="sm"
                  value={jumpToPage}
                  onChange={handleJumpInputChange}
                  onKeyPress={handleJumpInputKeyPress}
                  className="w-14"
                  placeholder="1"
                  disabled={effectiveCountLoading}
                />
                <Button
                  type="submit"
                  size="compact"
                  variant="accent"
                  disabled={effectiveCountLoading || !jumpToPage}
                >
                  Go
                </Button>
              </form>
              {jumpError && (
                <span className="text-xs text-webex-red-700 dark:text-webex-red-400 max-w-32">
                  {jumpError}
                </span>
              )}
            </div>
          )}

          <nav aria-label="Pagination" className="isolate inline-flex -space-x-px">
            <a
              href="#"
              tabIndex={0}
              aria-disabled={!prevEnabled}
              className={cn(
                "relative inline-flex items-center py-2 pr-3 border-b-4 border-b-transparent text-sm font-medium",
                prevEnabled
                  ? "text-webex-gray-1000 dark:text-white hover:border-b-webex-gray-300 dark:hover:border-b-webex-gray-600"
                  : "cursor-default text-webex-gray-400 dark:text-webex-gray-500",
              )}
              onClick={(e) => {
                e.preventDefault()
                if (prevEnabled) onPageChange?.(currentPage - 1)
              }}
            >
              Prev
            </a>

            {(() => {
              const pages = getVisiblePages()
              const elements: JSX.Element[] = []
              let last = 0
              for (const p of pages) {
                if (Math.abs(p - last) > 1) {
                  elements.push(
                    <span
                      key={`ellipsis-${p}`}
                      className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-webex-gray-600 dark:text-webex-gray-400"
                    >
                      …
                    </span>,
                  )
                }
                const active = currentPage === p
                elements.push(
                  <a
                    href="#"
                    tabIndex={0}
                    key={p}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "relative inline-flex items-center px-3 py-2 text-sm font-medium",
                      active
                        ? "border-b-4 border-b-webex-gray-1000 dark:border-b-white text-webex-gray-1000 dark:text-white"
                        : "border-b-4 border-b-transparent text-webex-gray-1000 dark:text-white hover:border-b-webex-gray-300 dark:hover:border-b-webex-gray-600",
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      onPageChange?.(p)
                    }}
                  >
                    {p}
                  </a>,
                )
                last = p
              }
              return elements
            })()}

            <a
              href="#"
              tabIndex={0}
              aria-disabled={!nextEnabled}
              className={cn(
                "relative inline-flex items-center py-2 pl-3 border-b-4 border-b-transparent text-sm font-medium",
                nextEnabled
                  ? "text-webex-gray-1000 dark:text-white hover:border-b-webex-gray-300 dark:hover:border-b-webex-gray-600"
                  : "cursor-default text-webex-gray-400 dark:text-webex-gray-500",
              )}
              onClick={(e) => {
                e.preventDefault()
                if (nextEnabled) onPageChange?.(currentPage + 1)
              }}
            >
              Next
            </a>
          </nav>
        </div>
      )}
    </div>
  )
}
